import { Fragment, type ReactNode } from 'react';
import { Button, Input } from '@/shared/ui/atoms';
import { EmailIcon, LockIcon, UserIcon } from '@/shared/ui/atoms';
import { HologramDivider } from '../HologramDivider';
import { orderCandidatesByLastAccount, type LoginMethod } from '../../lib/lastLoginMethod';
import { SuccessStep } from '../SuccessStep';
import type { LoginStepContentProps } from './loginStepView.types';
import * as s from './LoginStepForm.css';

const METHOD_LABEL: Record<LoginMethod, string> = {
  otp: '이메일로 인증코드 받기',
  passkey: '패스키로 로그인',
  password: '비밀번호로 로그인',
};

// 잠긴 인증코드 버튼이 자기 대기 문구를 가리키는 id. 매초 바뀌는 값이라 live region 으로
// 두면 스크린리더가 1초마다 읽어 방해가 되므로 describedby 로만 연결한다.
const OTP_COOLDOWN_HINT_ID = 'login-otp-cooldown-hint';

// 비밀번호 스텝의 확정된 인증 대상. 입력칸이 아니라 값이라 Input 아톰의 label 대신
// aria-labelledby 로 이름을 준다.
const IDENTITY_LABEL_ID = 'login-identity-label';

/** 스텝별 JSX 만 그린다. 상태·API 순서는 useLoginStepForm 이 갖고, 여기는 받은 값과 핸들러를 잇기만 한다. */
export function LoginStepContent({
  step,
  handleNameSubmit,
  nameInputRef,
  realName,
  setRealName,
  setNameError,
  setUnregistered,
  setCandidates,
  nameError,
  disabled,
  candidates,
  lastAccount,
  handleCandidateSelect,
  isTransitioning,
  savedCandidatesRef,
  setIdentifyResult,
  submitting,
  unregistered,
  onSignupNavigate,
  account,
  methodNotice,
  methodOrder,
  passkeyPending,
  otpResendCooldown,
  methodHandlers,
  handleBack,
  handleOtpSubmit,
  otpInputRef,
  otpCode,
  handleOtpChange,
  otpError,
  handleResendOtp,
  triggerStepTransition,
  passwordInputRef,
  password,
  setPassword,
  passwordError,
  handlePasswordSubmit,
  loading,
  identifyResult,
  supportsPasskey,
  handlePasskeyLogin,
  error,
  onForgotPassword,
}: LoginStepContentProps): ReactNode {
  switch (step) {
    case 'name':
      return (
        <form
          onSubmit={(e) => {
            void handleNameSubmit(e);
          }}
          className={s.stepFormContent}
        >
          <Input
            ref={nameInputRef}
            label="이름"
            type="text"
            placeholder="이름을 입력하세요"
            leftIcon={<UserIcon />}
            value={realName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setRealName(e.target.value);
              setNameError('');
              setUnregistered(false);
              setCandidates([]);
            }}
            error={nameError}
            disabled={disabled}
            required
          />
          {candidates.length > 0 && (
            <>
              <div className={s.duplicateNotice}>
                동명이인이 있습니다. 본인의 이메일을 선택해주세요.
              </div>
              <div className={s.candidateList}>
                {orderCandidatesByLastAccount(candidates, lastAccount).map((c) => (
                  <button
                    key={c.accountKey}
                    type="button"
                    className={s.candidateButton}
                    onClick={() => handleCandidateSelect(c)}
                    disabled={disabled}
                  >
                    <span className={s.candidateButtonIcon}>
                      <EmailIcon />
                    </span>
                    <span>{c.maskedEmail}</span>
                    {c.accountKey === lastAccount && (
                      <span className={s.lastAccountTag}>지난번 로그인</span>
                    )}
                  </button>
                ))}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                fullWidth
                onClick={() => {
                  setCandidates([]);
                  savedCandidatesRef.current = [];
                  setRealName('');
                  setIdentifyResult(null);
                }}
                disabled={isTransitioning}
              >
                이름 다시 입력
              </Button>
            </>
          )}
          {candidates.length === 0 && (
            <div className={s.buttonGroup}>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth
                loading={submitting}
                disabled={disabled}
              >
                다음
              </Button>
            </div>
          )}
          {unregistered && (
            // 가입 확인을 마치고 부서 배정을 기다리는 사람도 이름 단계에서는 찾지 않는다(87bac89b) —
            // 누구에게나 같은 고정 안내라 계정 존재를 드러내지 않는다.
            <p className={s.unregisteredHint}>
              가입 확인을 마쳤다면 팀 배정을 기다리는 중입니다. 팀장이나 피플팀에 요청해 주세요.
            </p>
          )}
          {unregistered && onSignupNavigate && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              fullWidth
              onClick={onSignupNavigate}
              disabled={isTransitioning}
            >
              회원가입
            </Button>
          )}
        </form>
      );

    case 'method':
      return (
        <div className={s.stepFormContent}>
          <div className={s.emailBadge} aria-label="확인된 이메일">
            <span className={s.emailBadgeIcon}>
              <EmailIcon />
            </span>
            <span className={s.emailBadgeLabel}>{account?.maskedEmail ?? ''}</span>
          </div>
          {methodNotice && (
            <p className={s.methodNotice} role="status">
              {methodNotice}
            </p>
          )}
          <div className={s.methodGroup}>
            {methodOrder.map((method, index) => {
              const isPrimary = index === 0;
              // 진행 표시는 그 방법이 실제로 돌고 있을 때만 — 비밀번호는 다음 스텝으로 넘길 뿐이다
              const pending =
                method === 'passkey' ? passkeyPending : method === 'otp' && submitting;
              // 발송이 실패하면 이 스텝에 머무르므로 쿨다운을 여기서도 보여준다 —
              // 안 그러면 잠금이 화면에 나타나지 않아 사용자가 계속 눌러 429 에 닿는다.
              const cooldown = method === 'otp' ? otpResendCooldown : 0;
              return (
                <Fragment key={method}>
                  <Button
                    type="button"
                    variant={isPrimary ? 'primary' : 'ghost'}
                    size={isPrimary ? 'lg' : 'sm'}
                    fullWidth
                    onClick={methodHandlers[method]}
                    loading={pending}
                    disabled={disabled || pending || cooldown > 0}
                    aria-describedby={cooldown > 0 ? OTP_COOLDOWN_HINT_ID : undefined}
                  >
                    {METHOD_LABEL[method]}
                  </Button>
                  {cooldown > 0 && (
                    <p id={OTP_COOLDOWN_HINT_ID} className={s.methodCooldownText}>
                      {cooldown}초 후 재시도 가능
                    </p>
                  )}
                  {isPrimary && <HologramDivider />}
                </Fragment>
              );
            })}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              fullWidth
              onClick={handleBack}
              disabled={isTransitioning}
            >
              이름 다시 입력
            </Button>
          </div>
        </div>
      );

    case 'otp':
      return (
        <form
          onSubmit={(e) => {
            void handleOtpSubmit(e);
          }}
          className={s.stepFormContent}
        >
          <div className={s.emailBadge} aria-label="인증코드 전송된 이메일">
            <span className={s.emailBadgeIcon}>
              <EmailIcon />
            </span>
            <span className={s.emailBadgeLabel}>{account?.maskedEmail ?? ''}</span>
          </div>
          <Input
            ref={otpInputRef}
            label="인증코드"
            type="text"
            placeholder="6자리 코드 입력"
            value={otpCode}
            onChange={handleOtpChange}
            error={otpError}
            disabled={disabled}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            className={s.otpInput}
          />
          {/* 6자리가 차면 저절로 제출된다 — 확인 버튼을 두지 않는다. 계정이 전부 회사 Google Workspace 라 받은편지함이 곧 Gmail 이다 */}
          <a
            className={s.gmailLink}
            href="https://mail.google.com/mail/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Gmail에서 코드 찾기 <span aria-hidden="true">↗</span>
          </a>
          <div className={s.resendContainer}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                void handleResendOtp();
              }}
              loading={submitting}
              disabled={disabled || otpResendCooldown > 0}
            >
              {otpResendCooldown > 0 ? `재발송 (${otpResendCooldown})` : '재발송'}
            </Button>
          </div>
          <HologramDivider />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            onClick={() => triggerStepTransition('method')}
            disabled={isTransitioning}
          >
            다른 방법으로 로그인
          </Button>
        </form>
      );

    case 'password':
      return (
        <form
          onSubmit={(e) => {
            void handlePasswordSubmit(e);
          }}
          className={s.stepFormContent}
        >
          <div>
            <span className={s.identityKicker} id={IDENTITY_LABEL_ID}>
              인증 대상
            </span>
            <div className={s.emailBadge}>
              <span className={s.emailBadgeIcon}>
                <EmailIcon />
              </span>
              {/* readOnly 라도 username 칸은 남겨야 비밀번호 관리자가 자격증명을 짝짓는다 */}
              <input
                className={s.identityValue}
                type="text"
                autoComplete="username"
                value={account?.maskedEmail ?? ''}
                readOnly
                aria-labelledby={IDENTITY_LABEL_ID}
              />
              <button
                type="button"
                className={s.identityChangeButton}
                onClick={() => triggerStepTransition('method')}
                disabled={isTransitioning}
              >
                변경
              </button>
            </div>
          </div>
          <Input
            ref={passwordInputRef}
            label="비밀번호"
            type="password"
            autoComplete="current-password"
            placeholder="비밀번호를 입력하세요"
            leftIcon={<LockIcon />}
            value={password}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            error={passwordError}
            disabled={disabled}
            required
          />
          <div className={s.buttonGroup}>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              loading={loading || submitting}
              disabled={disabled}
            >
              입장
            </Button>
          </div>
          <HologramDivider />
          {identifyResult?.hasPasskey && supportsPasskey && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              fullWidth
              onClick={() => {
                void handlePasskeyLogin();
              }}
              loading={passkeyPending}
              disabled={disabled || passkeyPending}
            >
              패스키로 로그인
            </Button>
          )}
          {error && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              fullWidth
              onClick={onForgotPassword}
              disabled={isTransitioning}
            >
              비밀번호를 잊으셨나요?
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            fullWidth
            onClick={handleBack}
            disabled={isTransitioning}
          >
            다른 방법으로 로그인
          </Button>
        </form>
      );

    case 'success':
      return (
        <SuccessStep
          title="[접속 승인]"
          message={
            <>
              플레이어 인증이 완료되었습니다.
              <br />
              시나리오에 접속합니다...
            </>
          }
        />
      );
  }
}
